<script setup>
// Ramp — the screen this app exists for.
//
// It is read between sets, from the floor, by someone with chalk on their hands
// and a heart rate. So it says ONE thing at a time: the set you are on, in the
// serif, with only the plates that CHANGE called out — as chips you can match to
// the rack and on a bar drawn to scale. Done sets fold into one "done" line;
// upcoming sets are one quiet line each; there is one button.
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButtons, IonButton, IonIcon } from '@ionic/vue';
import AppHeader from '../core/ui/AppHeader.vue';
import RampSet from '../components/RampSet.vue';
import { findLift, addAttempt, saveAttempt, attempts, storeReady } from '../store.js';
import { seedIfEmpty, adoptNewDefaults, currentKit, platesFor } from '../lib/seed.js';
import { fmt } from '../lib/loading.js';
import { rampFor } from '../lib/jump.js';
import { isNative } from '../core/native.js';
import { loadingMotion } from '../lib/prefs.js';
import { sessionAttempt, startOfDay } from '../lib/attempts.js';

const props = defineProps({ id: { type: [String, Number], default: null } });
const router = useRouter();
// Resolved from the ROUTER, never imported from router.js: that module imports
// product-routes.js, which imports this page, so a top-level `import { HOME }`
// closes a cycle and the binding is still uninitialised when this module runs.
// The whole app booted to a blank screen with no error.
const HOME = router.resolve({ name: 'lifts' }).path;

// The design's camera (screens.py), as the same kind of value an imported
// ionicon is — a data: URL IonIcon parses in place, so nothing is fetched.
const CAMERA = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>"
  + "<g fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'>"
  + "<rect x='3' y='7' width='13' height='10' rx='2'/><path d='M16 11l5-3v8l-5-3'/></g></svg>";

const ready = ref(false);
const kit = ref(null);
const lift = ref(null);

onMounted(async () => {
  await storeReady;
  await seedIfEmpty();
  await adoptNewDefaults();
  kit.value = currentKit();
  lift.value = findLift(props.id);
  ready.value = true;
  keepAwake();
});

const unit = computed(() => kit.value?.unit ?? 'kg');
const plates = computed(() => (kit.value ? platesFor(kit.value) : []));

const ramp = computed(() => {
  if (!ready.value || !lift.value || !kit.value) return null;
  if (!plates.value.length) return { ok: false, noPlates: true };
  if (!Number(lift.value.topSet)) return null;
  // Today's warm-ups and jump if they were changed, so this screen loads what
  // the Lifts row and the scheme preview promised.
  return rampFor(lift.value, kit.value, plates.value);
});

// WHERE YOU ARE, kept across a reload.
//
// A phone that locked during a three-minute rest and came back to the top of
// the ramp is worse than useless — it is wrong, and the lifter would load the
// wrong bar. Session storage rather than the record: which set you are on is
// today's session, not a fact about the lift. (The Lifts row reads the same
// key to say "In progress · set 4 of 7".)
const KEY = computed(() => `work-up:at:${props.id}`);
const current = ref(0);
watch(KEY, (k) => { current.value = Number(sessionStorage.getItem(k) || 0); }, { immediate: true });
watch(current, (n) => sessionStorage.setItem(KEY.value, String(n)));

const sets = computed(() => (ramp.value?.ok ? ramp.value.sets : []));
const finished = computed(() => !!ramp.value?.ok && current.value >= sets.value.length);
const now = computed(() => sets.value[current.value] ?? null);
const upcoming = computed(() => sets.value.slice(current.value + 1));
const stateOf = (i) => (i < current.value ? 'done' : i === current.value ? 'current' : 'upcoming');

// DONE, IN ONE LINE. Every set you have lifted, by weight — the most recent
// kept when there are too many for the line, the oldest traded for "…".
const doneAll = computed(() => sets.value.slice(0, current.value).map((s) => fmt(s.total)));
const doneText = computed(() => {
  let shown = doneAll.value.slice();
  while (shown.length > 1 && shown.join(' · ').length > 27) shown = shown.slice(1);
  return (shown.length < doneAll.value.length ? '… ' : '') + shown.join(' · ');
});

// KEEP THE SCREEN ON. Between sets is two to five minutes of not touching the
// phone, and a lifter should not have to wake it with chalked hands to read the
// next load. Released on the way out — holding a wake lock on a screen nobody
// is looking at is a battery bug.
let lock = null;
async function keepAwake() {
  try { lock = await navigator.wakeLock?.request('screen'); } catch { lock = null; }
}
const onVisible = () => { if (document.visibilityState === 'visible' && !lock) keepAwake(); };
document.addEventListener('visibilitychange', onVisible);
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisible);
  try { lock?.release?.(); } catch { /* already gone */ }
  lock = null;
});

// WHEN THIS RAMP SESSION BEGAN, kept beside the position in session storage and
// cleared with it. Set the first time the ramp opens in a session; if storage
// refuses, the start of today stands in for it.
const SINCE = computed(() => `work-up:since:${props.id}`);
const since = ref(0);
watch(SINCE, (k) => {
  try {
    const kept = Number(sessionStorage.getItem(k));
    if (kept > 0) { since.value = kept; return; }
    since.value = Date.now();
    sessionStorage.setItem(k, String(since.value));
  } catch { since.value = startOfDay(); }
}, { immediate: true });

// An attempt exists for a set once it has been lifted or filmed IN THIS
// SESSION. Matched on weight, because that is what identifies a set within one
// session's ramp — and only since the session began, because last week's 100 kg
// warm-up is not today's (lib/attempts.js). Going back and doing a set again
// today finds today's attempt and records nothing twice.
const attemptFor = (set) => sessionAttempt(attempts.value, { lift: props.id, weight: set.total, since: since.value });
const filmedSet = (set) => !!attemptFor(set)?.videoAssetId;

function record(set) {
  const existing = attemptFor(set);
  if (existing) return existing;
  return addAttempt({
    lift: lift.value.id,
    weight: set.total,
    reps: set.reps,
    repsTo: set.repsTo ?? null, // a ranged set records the range it was asked for
    performedAt: new Date().toISOString(),
  });
}

function next(set) {
  record(set);
  current.value += 1;
}

// BACK to an earlier set: only ever to one already reached, and it deletes
// nothing — the attempts stay recorded, and `record` finds them again by weight
// when that set's Next set is pressed a second time. The bar animates from this
// set's loading to that one's, like any change of the bar.
function goBack(i) {
  if (i < 0 || i >= current.value) return;
  current.value = i;
}

// Filming is owned by the Film screen, which is where the library pointer and
// every one of its failure states already live. The ramp's job is to say WHICH
// set, and to make sure the attempt exists for a clip to attach to.
function film(set) {
  const row = record(set);
  saveAttempt({ ...row, reps: set.reps, repsTo: set.repsTo ?? null });
  router.push(`/tabs/lifts/${props.id}/attempts`);
}

// Today's warm-ups, from beside the sets they change. Back comes here.
const goScheme = () => router.push(`/tabs/lifts/${props.id}/scheme`);

const useTop = (w) => { lift.value = { ...lift.value, topSet: w }; };
const goKit = () => router.push('/tabs/kit');
const goFilm = () => router.push(`/tabs/lifts/${props.id}/attempts`);
// The same destination, and the same `replace`, as the header's Back.
const goLifts = () => router.replace(HOME);
</script>

<template>
  <IonPage class="work-up lift-ramp">
    <!-- Film is a CRITICAL screen in the plan and this was its only door — and
         that door was the per-set button, which is hidden on web. So on web the
         screen was unreachable, and on a phone you could only get to it in the
         middle of a workout. Reviewing yesterday's top set should not require
         starting one. -->
    <AppHeader :title="lift?.name || 'Ramp'" :back="HOME" back-text="Lifts">
      <IonButtons slot="end">
        <IonButton aria-label="Filmed sets" @click="goFilm">
          <IonIcon slot="icon-only" class="cam" :icon="CAMERA" />
        </IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true">
      <div v-if="ready && !lift" class="state">
        <p class="say">That lift isn't here any more.</p>
        <div class="choices"><button type="button" class="wu-btn-quiet" @click="goLifts">Back to Lifts</button></div>
      </div>

      <div v-else-if="ramp && ramp.noPlates" class="state">
        <p class="say">No plates in your kit.</p>
        <p class="why">The ramp is built from the plates you own.</p>
        <div class="choices"><button type="button" class="wu-btn" @click="goKit">Open Kit</button></div>
      </div>

      <div v-else-if="ready && !ramp" class="state">
        <p class="say">No top set for this lift.</p>
        <p class="why">Enter one and the warm-ups fill in.</p>
        <div class="choices"><button type="button" class="wu-btn-quiet" @click="goLifts">Back to Lifts</button></div>
      </div>

      <div v-else-if="ramp && !ramp.ok" class="state" role="alert">
        <p v-if="ramp.top.reason === 'under-bar'" class="say">
          That's lighter than the bar with collars ({{ fmt(ramp.top.above) }} {{ unit }}).
        </p>
        <p v-else class="say">Your plates can't make {{ fmt(lift.topSet) }} {{ unit }}.</p>
        <div class="choices">
          <button
            v-for="w in [ramp.top.below, ramp.top.above].filter((x) => x != null)"
            :key="w" type="button" class="wu-btn-quiet" @click="useTop(w)"
          >
            Load {{ fmt(w) }} {{ unit }}
          </button>
        </div>
      </div>

      <div v-else-if="ramp && ramp.ok" class="ramp">
        <!-- Where you are: the count, what is done, and a bar of segments —
             done is ink, the set you are on is ink at half, to come is a line.
             Shape and weight, no hue. -->
        <div class="progress">
          <p class="where">
            <span class="count">{{ finished ? `All ${sets.length} sets` : `Set ${current + 1} of ${sets.length}` }}</span>
            <span v-if="doneAll.length" class="done" :aria-label="`Done: ${doneAll.join(', ')}`">done {{ doneText }}</span>
          </p>
          <!-- A DONE SEGMENT IS A WAY BACK to that set. It looks exactly as it
               did; only its tap area (44px, laid over the space around it) is
               new. The set you are on and the sets to come are not buttons:
               forward is Next set, which is what records a set. -->
          <div class="segs">
            <template v-for="(s, i) in sets" :key="i">
              <button
                v-if="i < current"
                type="button"
                class="seg done"
                :aria-label="`Back to set ${i + 1}, ${fmt(s.total)} ${unit}`"
                @click="goBack(i)"
              />
              <span v-else class="seg" :class="stateOf(i)" aria-hidden="true" />
            </template>
          </div>
        </div>

        <template v-if="!finished && now">
          <!-- ONE ELEMENT FOR EVERY SET, not one per set: its bar has to see the
               stack CHANGE to load the next set from the last one, plates off
               and on. A key per set remounted it on every Next set, and a fresh
               drawing has no "before" to animate from. -->
          <RampSet
            key="now"
            :set="now" state="current" :kit="kit" :plates="plates" :filmed="filmedSet(now)"
            :animate="loadingMotion"
          />

          <section class="next" :aria-label="upcoming.length ? 'Up next' : undefined">
            <!-- On the last set there is no "Up next" to share the row with,
                 and the link's 44px target reached up into the drawing, where
                 an "off" under the plates coming off sat on top of it. Alone,
                 it takes its own row. -->
            <div class="next-head" :class="{ solo: !upcoming.length }">
              <h2 v-if="upcoming.length" class="wu-label">Up next</h2>
              <button type="button" class="wu-link warmups" @click="goScheme">Today’s warm-ups</button>
            </div>
            <ol v-if="upcoming.length" class="rows">
              <RampSet
                v-for="(set, j) in upcoming"
                :key="`${current + 1 + j}-${set.total}`"
                :set="set" state="upcoming" :kit="kit" :plates="plates"
              />
            </ol>
          </section>

          <!-- ONE button, held on screen: on a short phone the Up next list
               would otherwise push the one thing you press between sets out
               of reach. -->
          <div class="go">
            <!-- BACK A SET, the camera's mirror on the left. Quiet, because
                 forward is the job; there, because a mis-tapped Next set
                 should cost one tap. Nothing recorded is undone: Next set
                 finds a lifted set's attempt by weight, so doing it again
                 does not record it twice. -->
            <!-- ALWAYS IN THE ROW, disabled on the first set. It used to appear
                 only from set 2, so the first Next set shrank the button under
                 the finger mid-tap — and iOS left the old button's paint behind
                 on top of the new one. A row that never changes shape can't. -->
            <button
              type="button"
              class="wu-btn-quiet step-back"
              :disabled="current === 0"
              :aria-label="current > 0 ? `Back to the ${fmt(sets[current - 1].total)} ${unit} set` : 'No earlier set'"
              @click="goBack(current - 1)"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M14.5 6l-6 6 6 6" />
              </svg>
            </button>
            <button type="button" class="wu-btn next-set" @click="next(now)">
              {{ now.isTop ? 'Finish lift' : 'Next set' }}
            </button>
            <!-- Native only, as before: the web cannot save to the library. -->
            <button
              v-if="isNative"
              type="button"
              class="wu-btn-quiet film"
              :aria-label="filmedSet(now) ? 'Film again' : 'Film this set'"
              @click="film(now)"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="7" width="13" height="10" rx="2" /><path d="M16 11l5-3v8l-5-3" />
              </svg>
            </button>
          </div>
        </template>

        <div v-else class="finished">
          <p class="weight-line">
            <span class="w">{{ fmt(lift.topSet) }}</span>
            <span class="per">{{ unit }} × {{ lift.topSetReps || 1 }}</span>
          </p>
          <p class="say">Lift done.</p>
          <div class="go">
            <!-- A Finish lift tapped by mistake is one tap from the top set. -->
            <button
              v-if="sets.length"
              type="button"
              class="wu-btn-quiet step-back"
              :aria-label="`Back to the ${fmt(sets[sets.length - 1].total)} ${unit} set`"
              @click="goBack(sets.length - 1)"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M14.5 6l-6 6 6 6" />
              </svg>
            </button>
            <button type="button" class="wu-btn next-set" @click="goLifts">Back to Lifts</button>
          </div>
          <button type="button" class="wu-link warmups" @click="goScheme">Today’s warm-ups</button>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<style scoped>
/* Values are screens.py `ramp()`'s. */
/* 22px as the design draws it. Page-qualified because ion-buttons' own scoped
   rule (`.sc-ion-buttons-ios-s ion-icon[slot=icon-only]`, 0-2-1) outranks a
   bare class and draws it at 28. */
.lift-ramp ion-icon.cam { font-size: 22px; }

/* NO BOTTOM PADDING ON THIS PAGE. app.css gives every ion-content the tab
   bar's clearance + 16px, but measured in iOS and Material mode the content
   already ends AT the bar (787 of 844), so that padding is spent twice. Here
   it mattered: Chrome insets a sticky element by its scroller's padding, so
   Next set floated 72px above the bar with the list scrolling past beneath
   it. The sticky `.go` carries its own bottom space instead. */
.lift-ramp ion-content { --padding-bottom: 0px; }

.ramp { padding: 0 var(--wu-pad); }

.progress { display: flex; flex-direction: column; gap: 8px; padding-top: 10px; }
.where {
  display: flex; justify-content: space-between; gap: 12px; margin: 0;
  font: 400 0.75rem/1.3 var(--face-mono); color: var(--dim); white-space: nowrap;
}
.count { flex: none; }
.done { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.segs { display: flex; gap: 4px; }
.seg { flex: 1 1 0; height: 3px; border-radius: 2px; background: var(--line); }
.seg.done { background: var(--ink); }
.seg.current { background: var(--ink); opacity: 0.45; }
/* A done segment as a button: drawn the same 3px bar, tapped over 44px — a
   transparent layer from just above it into the space below, and across half
   of each gap, so the row does not grow and the targets meet. */
button.seg {
  position: relative;
  min-width: 0; margin: 0; padding: 0; border: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
button.seg::after { content: ''; position: absolute; inset: -12px -2px -29px; }
button.seg:active { opacity: 0.5; }
button.seg:focus-visible { outline: 2px solid var(--ink); outline-offset: 4px; }

.next { display: flex; flex-direction: column; }
.next-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 17px; margin-bottom: 6px; }
/* A door, not an action: small, quiet, and a full 44px target that the
   header row does not grow for. */
.warmups { margin: -14px 0 -13px auto; font-size: 0.75rem; color: var(--dim); }
.next-head.solo { margin: 4px 0 0; }
.next-head.solo .warmups { margin: 0 0 0 auto; }
.rows { margin: 0; padding: 0; }

.go {
  position: sticky; bottom: 0; z-index: 1;
  display: flex; gap: 10px;
  margin: 0 calc(-1 * var(--wu-pad));
  padding: 16px var(--wu-pad) 14px;
  background: var(--bg);
}
.next-set { --wu-btn-h: 54px; flex: 1 1 auto; }
.film,
.step-back { --wu-btn-h: 54px; width: 54px; padding: 0; flex: none; }

.finished { display: flex; flex-direction: column; padding-top: 22px; }
.finished .go { position: static; margin: 12px calc(-1 * var(--wu-pad)) 0; }
.finished .warmups { margin: 0; align-self: flex-start; }
.weight-line { display: flex; align-items: baseline; gap: 10px; margin: 0; }
.w { font: 400 min(4rem, 17vw, 8.5vh)/1 var(--face-serif); letter-spacing: -0.01em; color: var(--ink); }
.per { font: 400 0.9375rem/1 var(--face-mono); color: var(--dim); }

/* The states that are not a ramp: said in the serif, in ink, with the way
   out as a button. No colour — a problem is words. */
.state { padding: 28px var(--wu-pad); display: flex; flex-direction: column; gap: 8px; }
.say { margin: 0; font: 400 1.5rem/1.2 var(--face-serif); color: var(--ink); max-width: 20ch; }
.finished .say { margin-top: 10px; font-style: italic; font-size: 1.25rem; color: var(--dim); }
.why { margin: 0; font: 400 0.8125rem/1.5 var(--face-mono); color: var(--dim); max-width: 36ch; }
.choices { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
</style>
